import { useEffect, useState, useMemo } from 'react';
import { Product } from '@repo/types';
import {
    useNextProductAvailability,
    useAvailabilities,
} from '@repo/widget-utils/services/hooks/product';
import { TZDate, tzdate } from '@repo/tzdate';
import { upcomingSelectedTourAtom } from 'src/state/upcomingSelectedTour.atom';
import { useAtom } from 'ximple';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { useBookingContext } from 'src/widgets/BookingContext';
import {
    setSelectedTimeSlot,
    setHasChosenDate,
    setSelectedProducts,
} from 'src/state/booking/bookingReducer';

export default function useManageTourAndAvailability(
    product: Product | null,
    productCatalogId: string,
) {
    const configurations = useConfigurations();
    const [selectedTour, setSelectedTour] = useAtom(upcomingSelectedTourAtom);

    const { selectedProducts, onToggleVisible, bookingDispatch, quantities, selectedProductOption } =
        useBookingContext();

    const { data: nextProductAvailability } = useNextProductAvailability(
        product?.id,
        configurations.includeClosedAvailability,
        selectedProductOption?.id,
    );

    const nextProductAvailabilityStart = useMemo(() => {
        return nextProductAvailability ? tzdate(nextProductAvailability.next, false) : undefined;
    }, [nextProductAvailability]);

    const [availabilitySearchPeriod, setAvailabilitySearchPeriod] = useState<{
        startDay: TZDate | null;
        endDay: TZDate | null;
    }>(() => {
        let startDay: TZDate | null = null;
        let endDay: TZDate | null = null;

        if (selectedTour && selectedTour.product?.id === productCatalogId) {
            startDay = tzdate(selectedTour.start).startOf('month');
            endDay = startDay.endOf('month').add(1, 'day').startOf('day');
        }

        return { startDay, endDay };
    });

    const config = useConfigurations();
    const { data: availabilities, isLoading } = useAvailabilities(
        product,
        availabilitySearchPeriod.startDay,
        availabilitySearchPeriod.endDay,
        config.includeClosedAvailability,
        quantities,
        selectedProductOption?.id,
    );

    useEffect(() => {
        let startDay: TZDate;
        let endDay: TZDate;

        if (configurations.openingDate) {
            const openingDate = tzdate(configurations.openingDate);
            const now = TZDate.now();

            startDay =
                nextProductAvailabilityStart && openingDate.isBefore(nextProductAvailabilityStart)
                    ? nextProductAvailabilityStart.startOf('month')
                    : openingDate.isBefore(now)
                      ? now.startOf('month')
                      : openingDate;

            endDay = startDay.endOf('month').add(1, 'day').startOf('day');
        } else if (nextProductAvailabilityStart) {
            startDay = nextProductAvailabilityStart.startOf('month').startOf('day');
            endDay = nextProductAvailabilityStart.endOf('month').add(1, 'day').startOf('day');
        } else {
            const now = TZDate.now();
            startDay = now.startOf('month');
            endDay = startDay.endOf('month').add(1, 'day').startOf('day');
        }

        setAvailabilitySearchPeriod({ startDay, endDay });
    }, [configurations.openingDate, nextProductAvailabilityStart, setAvailabilitySearchPeriod]);

    useEffect(() => {
        if (
            selectedTour &&
            selectedTour.product?.id === productCatalogId &&
            !selectedProducts?.length &&
            availabilities
        ) {
            const avail = availabilities?.find(
                (avail) =>
                    avail.id === selectedTour.id || avail.collectedIds.includes(selectedTour.id),
            );
            if (!avail) return;
            bookingDispatch(
                setSelectedTimeSlot({
                    product: avail,
                    label: avail.product?.title ?? '',
                }),
            );
            bookingDispatch(setSelectedProducts([avail]));
            bookingDispatch(setHasChosenDate(true));
            onToggleVisible(true, 'keyboard');
            setSelectedTour(null);
        }
    }, [
        selectedTour,
        onToggleVisible,
        setSelectedTour,
        productCatalogId,
        selectedProducts,
        availabilities,
        bookingDispatch,
    ]);

    return {
        nextProductAvailability,
        nextProductAvailabilityStart,
        availabilities,
        availabilitySearchPeriod,
        setAvailabilitySearchPeriod,
        isLoading,
    };
}
